// NotificationChannelsApi — the notification channel-config transport (contract 22 §6.2 rows 7-9),
// the only HTTP importer behind the Notifications pane (canonical api/, contract 03 §15).
//
// Built WIDE on purpose. A second consumer inside module 22 already exists — the Google-Sheet
// permissions surface (ORP-19876, `UsersPermissions.vue:608, :686, :703`) reads the same GET and
// writes the same PUT. Two facts follow, and both are pinned by the spec:
//
//   1. `getInformation` returns the WHOLE body, not `.channels`. `is_token_owner` (`:610`) and
//      `channels.email.google_allowed_domains` / `google_allowed_emails` (`:613-614`) live outside
//      `channels`; narrowing the return here would force that ticket to fork this class.
//   2. `updateValues` accepts an optional `field`. That consumer PUTs `{channel, field, values}`
//      (`:689-693, :706-710`) — a THIRD body shape contract §6.2 row 9 does not document.
//
// `suppressGlobalErrorHandler` is the class default: a settings pane owns its own error UX, and the
// `400` from `update/` is rendered as a structured list, never as a global toast (§6, BE-22-03).
import type { AxiosInstance } from 'axios'

import type { NotificationChannel, NotificationInformationDto } from '../models/channel.types'

import { appHttp, unwrap } from '@/shared/http'

export interface UpdateChannelValuesParams {
  channel: NotificationChannel
  values: readonly string[]
  /** ⚠ ORP-19876's third body shape — `'google_allowed_domains' | 'google_allowed_emails'` today.
   *  Omitted for the recipient/webhook write this pane performs. */
  field?: string
}

export class NotificationChannelsApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private readonly cfg = { suppressGlobalErrorHandler: true } as const

  /**
   * `GET /notifications/information/{w}/all/` — every channel's config in one payload.
   *
   * ⚠ `unwrap`, not `unwrapEnvelope`: this route answers a BARE `{channels:{…}}` with no
   * `{status,data}` wrapper (contract §6.2 row 7, BE-22-07). `unwrapEnvelope` would read
   * `res.data.data`, hand back `undefined`, and every `.channels` read would be silently empty —
   * no throw, no error state, just a pane that says nothing is configured. Asserted in the spec.
   *
   * The return type is the whole DTO for the reason at the top of this file.
   */
  async getInformation(workspaceId: number | string): Promise<NotificationInformationDto> {
    const response = await this.http.get<NotificationInformationDto>(
      `/notifications/information/${workspaceId}/all/`,
      this.cfg,
    )
    return unwrap(response) ?? {}
  }

  /** `PUT /notifications/information/{w}/enable/` `{channel, enabled}` (§6.2 row 8). */
  async setEnabled(
    workspaceId: number | string,
    params: { channel: NotificationChannel; enabled: boolean },
  ): Promise<void> {
    await this.http.put(`/notifications/information/${workspaceId}/enable/`, params, this.cfg)
  }

  /**
   * `PUT /notifications/information/{w}/update/` `{channel, values}` — or `{channel, field, values}`
   * when `field` is given (§6.2 row 9 plus ORP-19876's undocumented shape).
   *
   * `400` answers `{message, invalid_emails[]}`; it arrives as an `ApiError` from the interceptor
   * and the pane renders `invalidRecipients(error)` as a list (BE-22-03).
   */
  async updateValues(
    workspaceId: number | string,
    { channel, values, field }: UpdateChannelValuesParams,
  ): Promise<void> {
    await this.http.put(
      `/notifications/information/${workspaceId}/update/`,
      // `field` is omitted entirely rather than sent as `undefined`: the documented two-key body
      // must stay byte-identical to what the backend sees today.
      { channel, ...(field === undefined ? {} : { field }), values: [...values] },
      this.cfg,
    )
  }
}

export const notificationChannelsApi = new NotificationChannelsApi()
